import type {WorkforceProfileAnalysisDataInput} from '../../types/workforceProfileDetailsTypes';
import type {WorkforceProfileGuidedStepId, WorkforceProfileWizardSetup} from '../../types/workforceProfileWizardTypes';

export const validateGuidedSetup = (
  setup: WorkforceProfileWizardSetup,
  lookup: {personName: string; currentEmployerName: string}
): boolean =>
  !!setup.departmentId &&
  !!setup.seniority &&
  !!setup.targetProfileId &&
  !!lookup.personName.trim() &&
  !!lookup.currentEmployerName.trim();

export const validateGuidedStep = (
  stepId: WorkforceProfileGuidedStepId,
  analysisInput: WorkforceProfileAnalysisDataInput
): boolean => {
  const profile = analysisInput.candidateProfile;

  switch (stepId) {
    case 'setup':
    case 'research':
    case 'review':
      return true;
    case 'summary':
      return !!profile?.summary?.trim();
    case 'responsibilities':
      return !!profile?.responsibilities?.trim();
    case 'experience':
      return (
        analysisInput.workExperiences.length > 0 &&
        analysisInput.workExperiences.every((row) => !!row.roleTitle.trim())
      );
    case 'competencies':
      return (
        analysisInput.skillsCompetencies.length > 0 &&
        analysisInput.skillsCompetencies.every((row) => !!row.skill.trim() && !!row.evidence.trim())
      );
    case 'knowledge':
      return (
        analysisInput.knowledgeSkills.length > 0 &&
        analysisInput.knowledgeSkills.every((row) => !!row.skill.trim() && !!row.evidence.trim())
      );
    case 'education':
      return !!profile?.education?.trim();
    case 'languages':
      return true;
    case 'extras':
      return true;
    default:
      return true;
  }
};

export const validateGuidedReview = (analysisInput: WorkforceProfileAnalysisDataInput): string[] => {
  const issues: string[] = [];
  const profile = analysisInput.candidateProfile;

  if (!profile?.summary?.trim()) issues.push('summary');
  if (!profile?.responsibilities?.trim()) issues.push('responsibilities');
  if (!analysisInput.workExperiences.length) issues.push('experience');
  if (!analysisInput.skillsCompetencies.length) issues.push('competencies');
  if (!analysisInput.knowledgeSkills.length) issues.push('knowledge');
  if (!profile?.education?.trim()) issues.push('education');

  return issues;
};

export const collectSuggestedFieldKeys = (analysisInput: WorkforceProfileAnalysisDataInput): string[] => {
  const keys: string[] = [];
  const profile = analysisInput.candidateProfile;

  if (profile?.summary) keys.push('summary');
  if (profile?.responsibilities) keys.push('responsibilities');
  if (profile?.senioritySignals) keys.push('senioritySignals');
  if (profile?.education) keys.push('education');
  if (profile?.industryExperience) keys.push('industryExperience');
  if (profile?.workingStyleNotes) keys.push('workingStyleNotes');
  if (analysisInput.workExperiences.length) keys.push('experience');
  if (analysisInput.skillsCompetencies.length) keys.push('competencies');
  if (analysisInput.knowledgeSkills.length) keys.push('knowledge');
  if (analysisInput.languages.length) keys.push('languages');
  if (analysisInput.certifications.length) keys.push('certifications');

  return keys;
};
